import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

const LOGO_SRC = '/images/dharmar-3d-logo.png';

// Decide up front whether we should run the full animation or a static fallback.
function computeReducedMotion() {
  if (typeof window === 'undefined') return false;
  const prefersReduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const lowMemory = typeof navigator !== 'undefined' && navigator.deviceMemory && navigator.deviceMemory <= 2;
  const lowCores =
    typeof navigator !== 'undefined' && navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2;
  let noWebGL = false;
  try {
    const test = document.createElement('canvas');
    noWebGL = !(test.getContext('webgl') || test.getContext('experimental-webgl'));
  } catch (e) {
    noWebGL = true;
  }
  return Boolean(prefersReduced || lowMemory || lowCores || noWebGL);
}

// Shared scroll fraction across hero height (0 at top, 1 once scrolled ~one screen).
function useScrollFrac() {
  const ref = useRef(0);
  useEffect(() => {
    const update = () => {
      const h = window.innerHeight * 0.85;
      ref.current = h > 0 ? Math.min(Math.max(window.scrollY / h, 0), 1) : 0;
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);
  return ref;
}

// Animated 3D DharmaR emblem: logo plane + radial glow + pulsing rings + dust,
// reacting to mouse tilt with a gentle idle sway. Transparent canvas so the
// page's dark background and floating particles show through.
export const Hero3DLogo = ({ onActivate, audioActive = false }) => {
  const containerRef = useRef(null);
  const scrollFracRef = useScrollFrac();
  const audioActiveRef = useRef(audioActive);
  const [reduced] = useState(computeReducedMotion);
  const [pulse, setPulse] = useState(false); // fallback pulse
  const [scrollStyle, setScrollStyle] = useState({ opacity: 1, transform: 'scale(1)' });

  useEffect(() => {
    audioActiveRef.current = audioActive;
  }, [audioActive]);

  const activate = () => {
    setPulse(true);
    window.setTimeout(() => setPulse(false), 500);
    window.setTimeout(() => onActivate && onActivate(), 450);
  };

  // ---- Static fallback: reduced motion / low-power / no WebGL ----
  useEffect(() => {
    if (!reduced) return undefined;
    const update = () => {
      const f = scrollFracRef.current;
      setScrollStyle({ opacity: 1 - f * 0.9, transform: `scale(${1 - f * 0.15})` });
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, [reduced, scrollFracRef]);

  // ---- Full Three.js scene ----
  useEffect(() => {
    if (reduced) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    const isMobile = window.innerWidth < 640;
    let width = container.clientWidth;
    let height = container.clientHeight;

    const canvas = document.createElement('canvas');
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.display = 'block';
    container.appendChild(canvas);

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobile ? 1.5 : 2));
    renderer.setSize(width, height);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, width / height, 0.1, 100);
    camera.position.z = 9;

    const logoGroup = new THREE.Group();
    logoGroup.position.y = 1.0;
    scene.add(logoGroup);

    // ---- logo texture ----
    const logoBaseSize = isMobile ? 4.2 : 5.6;
    const texLoader = new THREE.TextureLoader();
    const logoTex = texLoader.load(LOGO_SRC, (tex) => {
      const aspect = tex.image.width / tex.image.height;
      logoMesh.geometry.dispose();
      logoMesh.geometry = new THREE.PlaneGeometry(logoBaseSize * aspect, logoBaseSize);
    });
    logoTex.anisotropy = renderer.capabilities.getMaxAnisotropy
      ? renderer.capabilities.getMaxAnisotropy()
      : 1;

    const logoGeo = new THREE.PlaneGeometry(logoBaseSize, logoBaseSize);
    const logoMat = new THREE.MeshBasicMaterial({ map: logoTex, transparent: true, depthWrite: false, side: THREE.DoubleSide });
    const logoMesh = new THREE.Mesh(logoGeo, logoMat);
    logoGroup.add(logoMesh);

    // ---- soft radial glow behind the logo ----
    const glowCanvas = document.createElement('canvas');
    glowCanvas.width = 512;
    glowCanvas.height = 512;
    const gctx = glowCanvas.getContext('2d');
    const ggrad = gctx.createRadialGradient(256, 256, 0, 256, 256, 256);
    ggrad.addColorStop(0, 'rgba(191,255,0,0.35)');
    ggrad.addColorStop(0.45, 'rgba(127,168,50,0.20)');
    ggrad.addColorStop(1, 'rgba(0,0,0,0)');
    gctx.fillStyle = ggrad;
    gctx.fillRect(0, 0, 512, 512);
    const glowTex = new THREE.CanvasTexture(glowCanvas);
    const glowMat = new THREE.SpriteMaterial({
      map: glowTex,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const glowSprite = new THREE.Sprite(glowMat);
    glowSprite.scale.set(logoBaseSize * 1.9, logoBaseSize * 1.9, 1);
    glowSprite.position.z = -0.5;
    logoGroup.add(glowSprite);

    // ---- pulsing rings ----
    const ringPalette = [0xbfff00, 0x7fa832, 0x9a4a12];
    const ringLife = 5.5;
    const ringSpawnInterval = 1.8;
    const ringPool = [];
    let ringColorIdx = 0;
    let lastRingSpawn = -1;

    function spawnRing(birthT) {
      const geo = new THREE.TorusGeometry(logoBaseSize * 0.42, 0.022, 8, 120);
      const color = ringPalette[ringColorIdx % ringPalette.length];
      ringColorIdx++;
      const mat = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.6,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.z = -0.3;
      mesh.userData.birth = birthT;
      logoGroup.add(mesh);
      ringPool.push(mesh);
    }

    // ---- dust particles (subtle, complements page-wide floating dots) ----
    const particleCount = isMobile ? 180 : 380;
    const positions = new Float32Array(particleCount * 3);
    const pColors = new Float32Array(particleCount * 3);
    const paletteColors = [
      new THREE.Color(0xbfff00),
      new THREE.Color(0x7fa832),
      new THREE.Color(0x9a4a12),
      new THREE.Color(0x5c3a1a),
    ];
    const particlePhase = new Float32Array(particleCount);
    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 36;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 36;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 30;
      particlePhase[i] = Math.random() * 100;
      const c = paletteColors[Math.floor(Math.random() * paletteColors.length)];
      pColors[i * 3] = c.r;
      pColors[i * 3 + 1] = c.g;
      pColors[i * 3 + 2] = c.b;
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    pGeo.setAttribute('color', new THREE.BufferAttribute(pColors, 3));
    const pMat = new THREE.PointsMaterial({
      size: 0.05,
      transparent: true,
      opacity: 0.6,
      vertexColors: true,
    });
    const particles = new THREE.Points(pGeo, pMat);
    scene.add(particles);
    const tmpColor = new THREE.Color();

    // ---- interaction: mouse tilt + hover/click raycast on the emblem ----
    let mouseX = 0;
    let mouseY = 0;
    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    let hovering = false;
    let pulseStart = -1;
    let activateTimer = null;

    const toNdc = (clientX, clientY) => {
      const rect = canvas.getBoundingClientRect();
      ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    };
    const hitLogo = (clientX, clientY) => {
      toNdc(clientX, clientY);
      raycaster.setFromCamera(ndc, camera);
      return raycaster.intersectObject(logoMesh, false).length > 0;
    };

    const onMouseMove = (e) => {
      mouseX = e.clientX / window.innerWidth - 0.5;
      mouseY = e.clientY / window.innerHeight - 0.5;
      hovering = hitLogo(e.clientX, e.clientY);
      container.style.cursor = hovering ? 'pointer' : 'default';
    };
    const onClick = (e) => {
      if (!hitLogo(e.clientX, e.clientY)) return;
      pulseStart = clock.getElapsedTime();
      if (activateTimer) clearTimeout(activateTimer);
      activateTimer = window.setTimeout(() => onActivate && onActivate(), 450);
    };
    window.addEventListener('mousemove', onMouseMove);
    container.addEventListener('click', onClick);

    const DEG = THREE.MathUtils.degToRad;
    const clock = new THREE.Clock();
    let camX = 0;
    let camY = 0;
    let tiltX = 0;
    let tiltY = 0;
    let rafId;

    function animate() {
      const t = clock.getElapsedTime();
      const scrollFrac = scrollFracRef.current;
      const active = audioActiveRef.current;
      // synthetic ~120bpm groove while the Listen Live player is active
      const beat = active ? 0.5 + 0.5 * Math.sin(t * Math.PI * 2 * 2) : 0;
      const kick = active ? Math.pow(Math.max(0, Math.sin(t * Math.PI * 2)), 8) : 0;

      camX += (mouseX * 0.7 - camX) * 0.04;
      camY += (-mouseY * 0.5 - camY) * 0.04;
      camera.position.x = camX;
      camera.position.y = camY;

      // gentle idle sway + mouse tilt (more reactive: wider angle + snappier easing)
      const maxTilt = DEG(30);
      const idleX = Math.sin(t * 0.5) * DEG(3);
      const idleY = Math.cos(t * 0.35) * DEG(4);
      const targetTiltX = -mouseY * maxTilt + idleX;
      const targetTiltY = mouseX * maxTilt + idleY;
      tiltX += (targetTiltX - tiltX) * 0.09;
      tiltY += (targetTiltY - tiltY) * 0.09;
      logoGroup.rotation.x = tiltX;
      logoGroup.rotation.y = tiltY;
      logoGroup.rotation.z = 0;
      // continuous rotation of the emblem around the vertical (Y) axis
      logoMesh.rotation.y = t * 0.4;

      // scroll hand-off: emblem drifts back, shrinks and fades
      // + very slow automatic orbit so it feels alive when the mouse is still
      logoGroup.position.x = Math.cos(t * 0.18) * 0.18;
      logoGroup.position.y = 1.0 + Math.sin(t * 0.18) * 0.12;
      logoGroup.position.z = -scrollFrac * 3.4;
      const heroFade = 1 - scrollFrac * 0.95;
      logoMat.opacity = heroFade;
      glowMat.opacity = heroFade * (0.95 + beat * 0.6 + kick * 0.5);

      // click pulse: quick grow-and-settle bump
      let pulseBump = 0;
      if (pulseStart >= 0) {
        const age = (t - pulseStart) / 0.5;
        if (age >= 1) {
          pulseStart = -1;
        } else {
          pulseBump = Math.sin(age * Math.PI) * 0.14;
        }
      }
      const beatBump = active ? beat * 0.03 + kick * 0.05 : 0;
      logoGroup.scale.setScalar((1 - scrollFrac * 0.35) * (1 + pulseBump + beatBump));

      camera.position.z = 9 - scrollFrac * 3.2;
      camera.lookAt(0, 0, 0);
      particles.rotation.y = t * 0.015;

      // spawn / update rings (faster + brighter while audio is active)
      const interval = active ? 0.85 : ringSpawnInterval;
      const spawnIdx = Math.floor(t / interval);
      if (spawnIdx !== lastRingSpawn) {
        lastRingSpawn = spawnIdx;
        spawnRing(t);
      }
      for (let i = ringPool.length - 1; i >= 0; i--) {
        const ring = ringPool[i];
        const lifeFrac = (t - ring.userData.birth) / ringLife;
        if (lifeFrac >= 1) {
          logoGroup.remove(ring);
          ring.geometry.dispose();
          ring.material.dispose();
          ringPool.splice(i, 1);
          continue;
        }
        ring.scale.setScalar(1 + lifeFrac * 2.1);
        ring.material.opacity = 0.6 * (1 - lifeFrac) * heroFade * (active ? 1.5 : 1);
      }

      // slow color cycle on dust
      const colorAttr = pGeo.attributes.color;
      for (let i = 0; i < particleCount; i++) {
        const phase = (t * 0.12 + particlePhase[i]) % paletteColors.length;
        const idxA = Math.floor(phase);
        const idxB = (idxA + 1) % paletteColors.length;
        tmpColor.copy(paletteColors[idxA]).lerp(paletteColors[idxB], phase - idxA);
        colorAttr.array[i * 3] = tmpColor.r;
        colorAttr.array[i * 3 + 1] = tmpColor.g;
        colorAttr.array[i * 3 + 2] = tmpColor.b;
      }
      colorAttr.needsUpdate = true;

      canvas.style.opacity = String(1 - scrollFrac);
      renderer.render(scene, camera);
      rafId = requestAnimationFrame(animate);
    }
    animate();

    // ---- resize ----
    const resize = () => {
      width = container.clientWidth;
      height = container.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    const ro = new ResizeObserver(resize);
    ro.observe(container);

    return () => {
      cancelAnimationFrame(rafId);
      if (activateTimer) clearTimeout(activateTimer);
      window.removeEventListener('mousemove', onMouseMove);
      container.removeEventListener('click', onClick);
      ro.disconnect();
      ringPool.forEach((r) => {
        r.geometry.dispose();
        r.material.dispose();
      });
      logoGeo.dispose();
      logoMat.dispose();
      logoTex.dispose();
      glowTex.dispose();
      glowMat.dispose();
      pGeo.dispose();
      pMat.dispose();
      renderer.dispose();
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
    };
  }, [reduced, onActivate, scrollFracRef]);

  if (reduced) {
    return (
      <div
        ref={containerRef}
        data-testid="hero-3d-logo"
        className="absolute inset-0 z-0 flex items-center justify-center overflow-hidden"
      >
        <button
          type="button"
          onClick={activate}
          aria-label="DharmaR — go to Releases"
          data-testid="hero-logo-static"
          className="relative -translate-y-6 cursor-pointer bg-transparent border-0 p-0 transition-transform duration-500"
          style={scrollStyle}
        >
          <span
            className={`absolute inset-0 rounded-full blur-3xl ${audioActive ? 'animate-pulse' : ''}`}
            style={{
              background:
                'radial-gradient(circle, rgba(191,255,0,0.35) 0%, rgba(127,168,50,0.18) 45%, rgba(0,0,0,0) 72%)',
              transform: 'scale(1.4)',
            }}
          />
          <img
            src={LOGO_SRC}
            alt="DharmaR emblem"
            className={`relative w-[75vw] max-w-[520px] md:w-[38vw] transition-transform duration-500 ${
              pulse ? 'scale-110' : audioActive ? 'animate-pulse scale-105' : 'scale-100'
            }`}
            style={{ filter: 'drop-shadow(0 0 28px rgba(191,255,0,0.35))' }}
            loading="eager"
          />
        </button>
      </div>
    );
  }

  return <div ref={containerRef} data-testid="hero-3d-logo" className="absolute inset-0 z-0" />;
};
